import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/useAuth";
import * as researchApi from "../api/researchApi";

/**
 * Hook que maneja TODO el estado del flujo de investigación.
 *
 * Es el "model" del frontend — los componentes Step1-5 son views que
 * leen este state y disparan acciones. Mantiene:
 *   - sessionId (UUID único por búsqueda)
 *   - step actual
 *   - formData (inputs del usuario)
 *   - responses (lo que devolvió el backend por paso)
 *   - history (conversación user/assistant para context)
 *   - loading / error
 *
 * Persiste automáticamente en localStorage; restaura al montar.
 *
 * Uso típico desde un componente:
 *   const session = useResearchSession();
 *   if (session.loading) return <Spinner />;
 *   if (session.responses[1]) return <Step2 ... />;
 *   await session.runDiscovery(query, useContext);
 */

const STORAGE_KEY = "conocelo_session_v3";

export function useResearchSession() {
  const { getToken } = useAuth();

  // ============================================================
  // Estado
  // ============================================================
  const [sessionId, setSessionId] = useState(null);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [responses, setResponses] = useState({});
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [restoredNotice, setRestoredNotice] = useState(false);
  // Cache por paso, indexado por una key que identifica el input.
  // Ej: stepCache[5] = { "MacBook Pro M4": <response>, "Dell XPS 15": <response> }
  // Sirve para no regenerar cuando el usuario vuelve a un producto que ya vio.
  const [stepCache, setStepCache] = useState({});

  // AbortController para cancelar el request en vuelo si llega otro nuevo
  // o si el componente se desmonta. Vive en ref porque cambiarlo no
  // necesita re-render.
  const abortRef = useRef(null);

  // ============================================================
  // Init: cargar de localStorage o crear sesión nueva
  // ============================================================
  useEffect(() => {
    const saved = loadFromStorage();
    if (saved?.formData?.initialQuery || saved?.formData?.product) {
      // Hay una sesión previa con contenido — restaurar
      setSessionId(saved.sessionId);
      setStep(saved.step || 1);
      setFormData(saved.formData || {});
      setResponses(saved.responses || {});
      setHistory(saved.history || []);
      setStepCache(saved.stepCache || {});
      setRestoredNotice(true);
    } else {
      // Primera vez o sin progreso — sesión nueva
      setSessionId(crypto.randomUUID());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================
  // Auto-save a localStorage cuando cambia algo relevante
  // ============================================================
  useEffect(() => {
    if (!sessionId) return;
    // Solo guardar si hay algún tipo de progreso. Revisamos las tres
    // colecciones porque en teoría podrían poblarse independientemente
    // (ej. bug futuro o feature que rellene history pre-formData).
    const hasProgress =
      Object.keys(formData).length > 0 ||
      Object.keys(responses).length > 0 ||
      history.length > 0;
    if (!hasProgress) return;
    saveToStorage({ sessionId, step, formData, responses, history, stepCache });
  }, [sessionId, step, formData, responses, history, stepCache]);

  // ============================================================
  // Acciones simples (mutaciones de estado sin red)
  // ============================================================
  const startNewSession = useCallback(() => {
    // Cancelar request si lo hay
    abortRef.current?.abort();
    clearStorage();
    setSessionId(crypto.randomUUID());
    setStep(1);
    setFormData({});
    setResponses({});
    setHistory([]);
    setStepCache({});
    setError(null);
    setRestoredNotice(false);
  }, []);

  const dismissRestoredNotice = useCallback(() => {
    setRestoredNotice(false);
  }, []);

  const updateFormField = useCallback((key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  /**
   * Borra la respuesta de un paso específico (útil para "regenerar" o
   * "buscar de nuevo desde aquí"). NO toca formData — el usuario puede
   * editar sus inputs y volver a disparar.
   */
  const resetStep = useCallback((stepNumber) => {
    abortRef.current?.abort();
    setResponses((prev) => {
      const next = { ...prev };
      delete next[stepNumber];
      return next;
    });
  }, []);

  const goToStep = useCallback((n) => {
    setStep(n);
  }, []);

  const confirmProduct = useCallback(
    (productName) => {
      // Si cambia el producto respecto al que ya estaba fijado, limpiamos las
      // responses de steps 2-5 (son específicas del producto anterior).
      // El stepCache se conserva → si el usuario vuelve al producto anterior,
      // el runner correspondiente encontrará cache hit y restaurará instantáneo.
      const currentProduct = formData.product;
      if (currentProduct && currentProduct !== productName) {
        abortRef.current?.abort();
        // Steps 2 y 3 tienen cache single-slot por producto. Si volvemos a un
        // producto ya visto, restauramos su resultado en lugar de dejarlo vacío
        // (si no, step 3 vuelve a mostrar el formulario "vacío" pidiendo todo).
        const cached2 = stepCache[2]?.[productName];
        const cached3 = stepCache[3]?.[productName];
        setResponses((prev) => {
          const next = { ...prev };
          delete next[2];
          delete next[3];
          delete next[4];
          delete next[5];
          if (cached2) next[2] = cached2;
          if (cached3) next[3] = cached3;
          return next;
        });
        // Estos formData quedaron "colgados" del producto anterior; también fuera.
        setFormData((prev) => ({
          ...prev,
          product: productName,
          specContext: "",
          requirements: "",
          budget: "",
          extraContext: "",
          selectedOptionNames: "",
          chosenProduct: "",
        }));
      } else {
        updateFormField("product", productName);
      }
      setStep(2);
    },
    [updateFormField, formData.product, stepCache]
  );

  // ============================================================
  // Runners de los 5 pasos (cada uno hace la llamada API)
  // ============================================================

  /**
   * Wrapper genérico que cancela request previo, marca loading,
   * llama la api, actualiza responses + history, maneja errores.
   */
  const runStep = useCallback(
    async ({ stepNumber, apiFn, body, userTurnText }) => {
      // Cancelar cualquier request en vuelo
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);

      try {
        // Solo mandamos el contexto de pasos <= al actual. Si el usuario avanzó
        // y luego retrocedió, los turnos de pasos posteriores pertenecen a una
        // rama abandonada y no deben contaminar esta llamada (bloatea el payload
        // y confunde al modelo). Turnos legacy sin `step` se conservan.
        const priorHistory = history
          .filter((h) => h.step == null || h.step <= stepNumber)
          .map(({ step, ...turn }) => turn); // strip `step` antes de enviar
        const requestBody = { ...body, sessionId, history: priorHistory };
        const response = await apiFn(getToken, requestBody, {
          signal: controller.signal,
        });

        // Validar que la respuesta tenga contenido útil. Si vino vacía,
        // no contaminamos history ni responses — el usuario debe reintentar
        // con los mismos inputs (el formData ya quedó actualizado en el
        // wrapper específico).
        const hasContent = response?.markdown || response?.toolOutput;
        if (!hasContent) {
          setError(
            "El modelo no devolvió contenido. Reintenta o reformula la consulta."
          );
          return null;
        }

        // Construir el turno assistant aproximado para historia futura.
        // Al guardar, descartamos también los turnos de pasos posteriores
        // (rama abandonada) y etiquetamos los nuevos con el step actual.
        const assistantContent = buildAssistantContent(response);
        const newHistory = [
          ...history.filter((h) => h.step == null || h.step <= stepNumber),
          { role: "user", content: userTurnText, step: stepNumber },
          { role: "assistant", content: assistantContent, step: stepNumber },
        ];

        setResponses((prev) => ({ ...prev, [stepNumber]: response }));
        setHistory(newHistory);
        return response;
      } catch (e) {
        // AbortError es normal cuando cancelamos request — no es error real
        if (e.name === "AbortError") return null;
        setError(e.message || String(e));
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [getToken, sessionId, history]
  );

  // Step 1: Discovery (modo inicial o refinamiento)
  const runDiscovery = useCallback(
    async (query, useContext, refinement) => {
      updateFormField("initialQuery", query);
      if (useContext !== undefined) updateFormField("useContext", useContext);

      const body = { query, useContext, refinement };
      const userTurnText = refinement
        ? `Refinamiento: ${refinement}`
        : `Búsqueda: ${query}${useContext ? `\nContexto: ${useContext}` : ""}`;

      return runStep({
        stepNumber: 1,
        apiFn: researchApi.discovery,
        body,
        userTurnText,
      });
    },
    [runStep, updateFormField]
  );

  // Step 2: Specs — con cache por producto
  const runSpecs = useCallback(
    async (specContext, options = {}) => {
      const { force = false } = options;
      updateFormField("specContext", specContext);

      // Cache hit: solo cuando no hay refinamiento y no forzamos
      const cacheKey = formData.product;
      const cached = !force && !specContext && stepCache[2]?.[cacheKey];
      if (cached) {
        setResponses((prev) => ({ ...prev, 2: cached }));
        return cached;
      }

      const body = { product: formData.product, specContext };
      const userTurnText = `Especificaciones para: ${formData.product}${
        specContext ? `\nContexto: ${specContext}` : ""
      }`;

      const result = await runStep({
        stepNumber: 2,
        apiFn: researchApi.specs,
        body,
        userTurnText,
      });

      // Solo cachear si NO fue una refinación (una refinación es específica al contexto)
      if (result && !specContext) {
        setStepCache((prev) => ({
          ...prev,
          2: { ...(prev[2] || {}), [cacheKey]: result },
        }));
      }
      return result;
    },
    [runStep, updateFormField, formData.product, stepCache]
  );

  // Step 3: Options
  const runOptions = useCallback(
    async (requirements, budget, extraContext) => {
      updateFormField("requirements", requirements);
      updateFormField("budget", budget);
      updateFormField("extraContext", extraContext);
      const body = {
        product: formData.product,
        requirements,
        budget,
        extraContext,
      };
      const userTurnText = `Opciones para: ${formData.product}\nRequisitos: ${
        requirements || "sin restricciones"
      }\nPresupuesto: ${budget || "no especificado"}`;

      const result = await runStep({
        stepNumber: 3,
        apiFn: researchApi.options,
        body,
        userTurnText,
      });

      // Cache single-slot por producto (última búsqueda gana). Sirve para
      // restaurar el resultado si el usuario vuelve a este producto sin tener
      // que re-pedir opciones. "Buscar de nuevo" pasa por resetStep + nueva
      // llamada, que sobreescribe este slot.
      if (result) {
        setStepCache((prev) => ({
          ...prev,
          3: { ...(prev[3] || {}), [formData.product]: result },
        }));
      }
      return result;
    },
    [runStep, updateFormField, formData.product]
  );

  // Step 4: Compare — con cache por combinación de opciones seleccionadas
  const runCompare = useCallback(
    async (selectedNames, options = {}) => {
      const { force = false } = options;
      updateFormField("selectedOptionNames", selectedNames);

      // Cache key = la combinación exacta de nombres. Si cambia una selección,
      // es otra key → otra llamada.
      const cacheKey = selectedNames;
      const cached = !force && stepCache[4]?.[cacheKey];
      if (cached) {
        setResponses((prev) => ({ ...prev, 4: cached }));
        return cached;
      }

      const body = { product: formData.product, selectedNames };
      const userTurnText = `Comparar: ${selectedNames}`;

      const result = await runStep({
        stepNumber: 4,
        apiFn: researchApi.compare,
        body,
        userTurnText,
      });

      if (result) {
        setStepCache((prev) => ({
          ...prev,
          4: { ...(prev[4] || {}), [cacheKey]: result },
        }));
      }
      return result;
    },
    [runStep, updateFormField, formData.product, stepCache]
  );

  // Step 5: Where to buy — con cache por producto elegido
  const runWhereToBuy = useCallback(
    async (chosen, options = {}) => {
      const { force = false } = options;
      updateFormField("chosenProduct", chosen);

      // Cache hit: restaurar sin llamada al API
      const cached = !force && stepCache[5]?.[chosen];
      if (cached) {
        setResponses((prev) => ({ ...prev, 5: cached }));
        return cached;
      }

      // Cache miss (o force): llamada normal + guardar en cache
      const body = { product: formData.product, chosen };
      const userTurnText = `Dónde comprar: ${chosen}`;

      const result = await runStep({
        stepNumber: 5,
        apiFn: researchApi.whereToBuy,
        body,
        userTurnText,
      });

      if (result) {
        setStepCache((prev) => ({
          ...prev,
          5: { ...(prev[5] || {}), [chosen]: result },
        }));
      }
      return result;
    },
    [runStep, updateFormField, formData.product, stepCache]
  );

  // ============================================================
  // Cleanup: abortar request en vuelo cuando el componente desmonte
  // ============================================================
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  // ============================================================
  // API pública del hook
  // ============================================================
  return {
    // Estado
    sessionId,
    step,
    formData,
    responses,
    history,
    loading,
    error,
    restoredNotice,

    // Acciones simples
    startNewSession,
    dismissRestoredNotice,
    updateFormField,
    goToStep,
    confirmProduct,
    resetStep,

    // Runners de pasos
    runDiscovery,
    runSpecs,
    runOptions,
    runCompare,
    runWhereToBuy,
  };
}

// ============================================================
// Helpers internos (no exportados)
// ============================================================

/**
 * Construye el campo `content` aproximado del turno assistant para
 * mandar en future requests como history.
 *
 * IMPORTANTE: NO incluimos blocks tipo `tool_use` aunque el response
 * los tuviera. Anthropic requiere que cada tool_use en historia tenga
 * un tool_result correspondiente en el siguiente turno user — y nosotros
 * no tenemos uno porque nuestras tools son "estructurar output", no
 * "ejecutar función externa". Si los incluyéramos, la API rechaza con
 * "tool_use ids were found without tool_result blocks".
 *
 * Solución: convertimos el toolOutput a texto plano dentro del turno
 * assistant. El modelo conserva el contexto visualmente (puede leer
 * lo que sugirió antes) sin romper el contrato del protocolo.
 */
function buildAssistantContent(response) {
  const blocks = [];
  if (response.markdown) {
    blocks.push({ type: "text", text: response.markdown });
  }
  if (response.toolOutput) {
    blocks.push({
      type: "text",
      text:
        "[Datos estructurados que devolví]:\n" +
        JSON.stringify(response.toolOutput, null, 2),
    });
  }
  return blocks.length > 0 ? blocks : [{ type: "text", text: "(sin contenido)" }];
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveToStorage(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Quota exceeded u otro fail — silenciar, no es crítico
  }
}

function clearStorage() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
